import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { CustomInput, CustomTextarea, ToggleSwitch } from "@components/Forms";
import { LicensePolicySteps } from "@components/LicensePolicy";
import { LicensePolicyExceptionRuleTable } from "@components/Tables";
import Breadcrumb from "@components/UI/Breadcrumb";
import queryKeys from "@configs/react-query/query-key";
import { default as useSuccessToast, showErrorToast } from "@configs/react-toastify/snackbar";
import { CHARACTER_LENGTH, TOAST } from "@constants/index";
import { getPolicyStepColors } from "@constants/licensePolicy";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { validateDuplicateRules } from "@utils/licensePolicy";
import { handleLicensePolicyMutationError } from "@utils/error";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

const AddLicensePolicy = () => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const navigate = useNavigate();

  const breadcrumbItems = [
    { label: t("license-policy.license-policy"), href: "/license-policy" },
    { label: t("license-policy.create-new-policy") },
  ];

  // Form state
  const [policyData, setPolicyData] = useState({
    policyName: "",
    remark: "",
    globalPolicy: false,
  });

  // Validation state
  const [errors, setErrors] = useState({});
  const [colorErrors, setColorErrors] = useState({});
  const [stepNumberErrors, setStepNumberErrors] = useState({});
  const [policySteps, setPolicySteps] = useState({});

  const [licensePolicyRules, setLicensePolicyRules] = useState([]);

  // Track which step is currently expanded
  const [expandedStep, setExpandedStep] = useState(null);

  const handleStepChange = useCallback((index, stepData) => {
    setPolicySteps((prev) => {
      const newSteps = { ...prev };

      // Update the step data
      newSteps[index] = stepData;

      return newSteps;
    });

    // Update licensePolicyRules to reflect the new step color and approval
    setLicensePolicyRules((prevRules) => {
      return prevRules.map((rule) => {
        if (Number(rule.policyStep) === stepData.stepNumber) {
          return {
            ...rule,
            policyStepColor: stepData.color, // Update with new color
            licenseUseApprovalCd: stepData.approval, // Update with new approval
          };
        }
        return rule;
      });
    });
  }, []);

  const addNewStep = () => {
    // Check if maximum steps limit reached
    const stepsCount = Object.keys(policySteps).length;
    // Don't add more than 5 steps
    if (stepsCount >= 5) return;

    // Find the next available color
    const usedColors = Object.values(policySteps).map((step) => step.color);
    const allColors = getPolicyStepColors(t).map((color) => color.value);
    const availableColor = allColors.find((color) => !usedColors.includes(color)) || allColors[0];

    // Get the next available index for the object key (should always be sequential now)
    const nextIndex = stepsCount > 0 ? Math.max(...Object.keys(policySteps).map(Number)) + 1 : 0;

    // Get the next step number (1, 2, 3, 4, 5)
    const existingStepNumbers = Object.values(policySteps).map((step) => step.stepNumber);
    let nextStepNumber = 1;

    // Find the next available step number
    while (existingStepNumbers.includes(nextStepNumber)) {
      nextStepNumber++;
    }

    setPolicySteps((prev) => ({
      ...prev,
      [nextIndex]: {
        stepNumber: nextStepNumber,
        color: availableColor,
        approval: "DISAPPROVAL",
        remark: "",
        licensePolicyStepItems: [],
      },
    }));
    // Auto-expand the new step by index, not step number
    setExpandedStep(nextIndex);
  };

  const deleteStep = (index) => {
    setPolicySteps((prev) => {
      const newSteps = { ...prev };
      const deletedStep = newSteps[index];
      if (!deletedStep) return prev;
      delete newSteps[index];

      const remainingSteps = Object.values(newSteps);
      if (remainingSteps.length > 0) {
        const minStep = remainingSteps.reduce(
          (prev, curr) => (curr.stepNumber < prev.stepNumber ? curr : prev),
          remainingSteps[0],
        );

        setLicensePolicyRules((prevRules) =>
          prevRules.map((rule) => {
            const exists = remainingSteps.some((s) => s.stepNumber === rule.policyStep);
            if (exists) return rule;

            return {
              ...rule,
              policyStep: minStep.stepNumber,
              policyStepColor: minStep.color,
              licenseUseApprovalCd: minStep.approval,
            };
          }),
        );
      }

      return newSteps;
    });

    // Update expanded step if needed
    if (expandedStep === index) {
      // If the deleted step was expanded, set to null
      setExpandedStep(null);
    }
  };

  // Handle step expansion - only allow one step to be expanded at a time
  const handleStepExpand = useCallback(
    (index) => {
      // Only allow toggling if the step is actually different or if we're expanding a new step
      if (expandedStep !== index) {
        setExpandedStep(index);
      } else {
        setExpandedStep(null);
      }
      // If expandedStep === index, don't change it (prevent accidental toggling during re-renders)
    },
    [expandedStep],
  );

  useEffect(() => {
    validateStepNumbers();
    validateStepColors();
  }, [policySteps]);

  const validateStepNumbers = useCallback(() => {
    const stepCount = {};
    const newStepErrors = {};

    Object.entries(policySteps).forEach(([index, step]) => {
      const stepNum = step.stepNumber;
      if (!stepCount[stepNum]) {
        stepCount[stepNum] = [];
      }
      stepCount[stepNum].push(index);
    });

    Object.entries(stepCount).forEach(([stepNum, indices]) => {
      if (indices.length > 1) {
        indices.forEach((idx) => {
          newStepErrors[idx] = "license-policy.error-duplicate-step-number";
        });
      }
    });

    setStepNumberErrors(newStepErrors);
    return Object.keys(newStepErrors).length === 0;
  }, [policySteps]);

  const validateStepColors = useCallback(() => {
    const colorCount = {};
    const newColorErrors = {};

    Object.entries(policySteps).forEach(([index, step]) => {
      const color = step.color;
      if (!color) return;

      if (!colorCount[color]) {
        colorCount[color] = [];
      }
      colorCount[color].push(index);
    });

    Object.entries(colorCount).forEach(([color, indices]) => {
      if (indices.length > 1) {
        indices.forEach((idx) => {
          newColorErrors[idx] = "license-policy.error-duplicate-step-color";
        });
      }
    });

    setColorErrors(newColorErrors);
    return Object.keys(newColorErrors).length === 0;
  }, [policySteps]);

  const validateForm = () => {
    const newErrors = {};

    if (!policyData.policyName || policyData.policyName.trim() === "") {
      newErrors.policyName = "license-policy.policy-name-required";
    }

    setErrors(newErrors);

    // Validate duplicate rules
    const isRulesValid = validateDuplicateRules(licensePolicyRules, t);

    return (
      Object.keys(newErrors).length === 0 &&
      Object.keys(colorErrors).length === 0 &&
      Object.keys(stepNumberErrors).length === 0 &&
      isRulesValid
    );
  };

  const handleSave = () => {
    if (!validateForm()) {
      return;
    }

    // Map policySteps object -> array with expected fields
    const licensePolicySteps = Object.entries(policySteps).map(([stepKey, step]) => ({
      db: false,
      delete: false,
      policyStep: step.stepNumber,
      policyStepColor: step.color,
      licenseUseApprovalCd: step.approval,
      remark: step.remark,
      licensePolicyStepItems: (step.licensePolicyStepItems || []).map((item) => ({
        db: false,
        delete: false,
        licensePolicyCd: item.code,
      })),
    }));

    const bodyRequest = {
      db: false,
      delete: false,
      policyName: policyData.policyName?.trim(),
      remark: policyData.remark,
      globalFlag: policyData.globalPolicy ? "Y" : "N",
      licensePolicySteps,
      licensePolicyRules,
    };
    createLicensePolicy.mutate(bodyRequest);
  };

  const handleCancel = () => {
    navigate("/license-policy");
  };

  const createLicensePolicy = useMutationData(
    queryKeys.createLicensePolicy,
    {
      method: "PUT",
    },
    {
      onSuccess: (response) => {
        useSuccessToast(TOAST.success, t("license-policy.license-policy-created-success"));
        navigate("/license-policy");
      },
      onError: (error) => {
        console.error("Error during create project:", error);
        handleLicensePolicyMutationError(
          error,
          t,
          showErrorToast,
          t("license-policy.license-policy-created-error"),
        );
        setLoading(false);
      },
    },
  );

  return (
    <div className={`flex h-full w-full flex-col ${colors.bgSecondary}`} data-testid="add-license-policy-page">
      {/* Breadcrumb */}
      <div className="flex items-center text-sm">
        <Breadcrumb items={breadcrumbItems} />
      </div>
      {/* Header */}
      <div className="flex items-center justify-between py-4">
        <div className="flex items-center gap-4">
          <h1 className={`text-xl font-semibold ${colors.textPrimary} uppercase`}>
            {t("license-policy.create-new-policy")}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <OutlineButton onClick={handleCancel}>{t("common.cancel-button")}</OutlineButton>
          <PrimaryButton data-testid="add-license-policy-save-button" disabled={policyData.policyName?.trim() === ""} onClick={handleSave}>{t("common.save-button")}</PrimaryButton>
        </div>
      </div>

      {/* Policy Information - Standalone Section */}
      <div className={`${colors.bgPrimary} rounded-lg p-6 border ${colors.borderPrimary}`} data-testid="add-license-policy-form">
        <h2 className={`text-md font-medium ${colors.textPrimary} mb-4 uppercase`}>{t("license-policy.policy-information")}</h2>
        <div className="grid grid-cols-12 gap-6">
          {/* Policy Name */}
          <div className="col-span-3">
            <label className={`block text-sm ${colors.textSubTitle} font-medium mb-2`}>
              {t("license-policy.policy-name")} <span className={colors.textRequired}>*</span>
            </label>
            <CustomInput
              value={policyData.policyName}
              onChange={(e) => {
                setPolicyData((prev) => ({ ...prev, policyName: e.target.value }));
                // Clear error when user starts typing
                if (errors.policyName) {
                  setErrors((prev) => ({ ...prev, policyName: "" }));
                }
              }}
              placeholder={t("license-policy.enter-policy-name")}
              size="md"
              maxLength={100}
            />
            {errors.policyName && <p className={`${colors.textDanger} text-sm mt-1`}>{t(errors.policyName)}</p>}
          </div>

          {/* Remark */}
          <div className="col-span-7">
            <label className={`block text-sm ${colors.textSubTitle} font-medium mb-2`}>{t("license-policy.remark")}</label>
            <CustomTextarea
              value={policyData.remark}
              onChange={(e) => setPolicyData((prev) => ({ ...prev, remark: e.target.value }))}
              placeholder={t("license-policy.write-remark")}
              rows={1}
              size="md"
              maxLength={CHARACTER_LENGTH.COMMENT}
            />
            <p className={`text-xs ${colors.textCharacterCount} mt-1`} data-testid="add-license-policy-remark-char-count">
              {(policyData.remark || "").length}/{CHARACTER_LENGTH.COMMENT}
            </p>
          </div>

          {/* Global Policy */}
          <div className="col-span-2 flex">
            <div className="flex flex-col">
              <label className={`block text-sm ${colors.textSubTitle} font-medium mb-2`}>
                {t("license-policy.default-policy")}
              </label>
              <div className="mt-2">
                <ToggleSwitch
                  checked={policyData.globalPolicy}
                  onChange={() => setPolicyData((prev) => ({ ...prev, globalPolicy: !prev.globalPolicy }))}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* License Policy Item List Container */}
      <LicensePolicySteps
        policySteps={policySteps}
        expandedStep={expandedStep}
        onStepChange={handleStepChange}
        onDeleteStep={deleteStep}
        onStepExpand={handleStepExpand}
        addNewStep={addNewStep}
        colors={colors}
        colorErrors={colorErrors}
        stepNumberErrors={stepNumberErrors}
      />

      {/* License Policy Exception Rule List */}
      <div className={`mt-6 ${colors.bgPrimary} rounded-lg p-6 border ${colors.borderPrimary}`}>
        <h2 className={`text-md font-medium ${colors.textPrimary} mb-4 uppercase`}>
          {t("license-policy.license-policy-rules-exception-list")}
        </h2>
        <LicensePolicyExceptionRuleTable
          data={licensePolicyRules}
          className="w-full"
          onDataChange={(rows) => setLicensePolicyRules(rows.map((r) => ({ ...r, db: false, delete: false })))}
          maxRowsPerPage={10}
          isEditMode={true}
          policySteps={policySteps}
        />
      </div>
    </div>
  );
};

export default AddLicensePolicy;
